import React, { useState } from 'react';
import { features, cartLabels } from '@/data/content';
import { trackEvent } from '@/Services/analytics';
import { menuItems } from '@/data/foods';
import { useAddToBasket } from '@/hooks/useAddToBasket';
import AddToBasketFull from '@/components/ui/AddToBasketFull';
import AddToBasketCompact from '@/components/ui/AddToBasketCompact';
import OpenTimingPopup from './OpenTimingPopup';
import { useToast } from '@/Context/ToastContext';
import { STORAGE_EVENT_DATE } from '@/hooks/useStorageEvent';

interface AddItemWithDatePopupProps {
    item: { id: number; type: 'food' | 'menu' };
    minQuantity?: number;
    maxQuantity?: number;
    price: number;
    isAvailable?: boolean;
    compact?: boolean;
}

const AddItemWithDatePopup: React.FC<AddItemWithDatePopupProps> = ({
    item,
    minQuantity = 1,
    maxQuantity: propsMaxQuantity,
    price,
    isAvailable = true,
    compact = false,
}) => {
    const { id, type } = item;
    const maxQty = propsMaxQuantity ?? 99;
    const [quantity, setQuantity] = useState(minQuantity);
    const [popupOpen, setPopupOpen] = useState(false);
    const { showToast } = useToast();

    if (!features.order) return null;

    const foodData = type === 'food' ? menuItems.find((f) => f.id === id) : undefined;
    const productName = foodData?.item_name || foodData?.name || `Product ${id}`;

    const {
        isAdding,
        addToBasket,
        checkDateAvailability,
        setIsAdding,
    } = useAddToBasket({
        id,
        price,
        productName,
        isMenu: type === 'menu',
        weightPercentage: foodData?.weight_percentage,
        portionLabel: foodData?.portion_label,
    });

    const openPopup = () => {
        setPopupOpen(true);
    };

    const handleAdd = async () => {
        const dateStr = localStorage.getItem('selectedServiceDate');
        if (!isAvailable) return;

        if (!dateStr) {
            openPopup();
            return;
        }

        setIsAdding(true);
        try {
            const reqQty = compact ? 1 : quantity;
            const canAdd = await checkDateAvailability(dateStr, reqQty);
            if (canAdd) {
                addToBasket(reqQty);
                setQuantity(minQuantity);
            } else {
                openPopup();
            }
        } catch {
            showToast('error', cartLabels.validation_error_label);
            openPopup();
        } finally {
            setIsAdding(false);
        }
    };

    const handleSelect = async (date: Date) => {
        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        setIsAdding(true);
        try {
            const reqQty = compact ? 1 : quantity;
            const canAdd = await checkDateAvailability(iso, reqQty);
            if (canAdd) {
                localStorage.setItem('selectedServiceDate', iso);
                window.dispatchEvent(new CustomEvent(STORAGE_EVENT_DATE));
                addToBasket(reqQty, iso);
                setQuantity(minQuantity);
            } else {
                showToast('error', cartLabels.validation_error_label);
            }
        } catch {
            showToast('error', cartLabels.validation_error_label);
        } finally {
            setIsAdding(false);
        }
    };

    const handleDecrement = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setQuantity((q) => Math.max(minQuantity, q - 1));
        trackEvent('menu_quantity_decrease', { item_id: id, item_name: productName });
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setQuantity((q) => Math.min(maxQty, q + 1));
        trackEvent('menu_quantity_increase', { item_id: id, item_name: productName });
    };

    return (
        <>
            {compact ? (
                <AddToBasketCompact disabled={!isAvailable} validating={isAdding} onClick={handleAdd} />
            ) : (
                <AddToBasketFull
                    quantity={quantity}
                    minQuantity={minQuantity}
                    maxQuantity={maxQty}
                    disabled={!isAvailable}
                    validating={isAdding}
                    onDecrement={handleDecrement}
                    onIncrement={handleIncrement}
                    onAdd={handleAdd}
                />
            )}
            <OpenTimingPopup
                isOpen={popupOpen}
                onClose={() => setPopupOpen(false)}
                onSelect={handleSelect}
                applyLabel="Available"
                onFetchCount={async (date) => {
                    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    try {
                        const canAdd = await checkDateAvailability(iso, compact ? 1 : quantity);
                        return canAdd ? 1 : 0;
                    } catch {
                        return 0;
                    }
                }}
            />
        </>
    );
};

export default AddItemWithDatePopup;
